import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import api from "../Services/api";
import { useToast } from "../Hooks/toast";

type RefreshOptions = { notify?: boolean };

interface SupportContextData {
  unread: number;
  setUnread: (value: number) => void;
  refreshUnread: (opts?: RefreshOptions) => Promise<number | null>;
}

const SupportContext = createContext<SupportContextData>({} as SupportContextData);

export const SupportProvider: React.FC = ({ children }) => {
  const [unread, setUnread] = useState<number>(0);
  const { addToast } = useToast();

  const refreshUnread = useCallback(async (opts?: RefreshOptions) => {
    try {
      const { data } = await api.get("/support/unreads");
      const count = typeof data === "number" ? data : parseInt(data, 10);
      const safeCount = isNaN(count) ? 0 : Math.max(0, count);
      setUnread(safeCount);

      if ((opts?.notify ?? true) && safeCount > 0) {
        addToast({
          type: "info",
          title: "Suporte",
          description: `Você tem ${safeCount} mensagens novas do suporte`,
          linkTo: "/suporte",
          linkLabel: "Abrir suporte",
        });
      }

      return safeCount;
    } catch (e) {
      return null;
    }
  }, [addToast]);

  const value = useMemo(() => ({ unread, setUnread, refreshUnread }), [unread, refreshUnread]);

  return (
    <SupportContext.Provider value={value}>{children}</SupportContext.Provider>
  );
};

export const useSupport = () => {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error("useSupport must be used within SupportProvider");
  return ctx;
};
